import { useMemo } from 'react';
import { StanDataRow } from '@/components';
import type { DataRowActionButton } from '@/components/core-components/StanDataRow/StanDataRow.types';
import { buildAutomationColumns, getGridTemplate } from '../logic/automationRow.logic';
import { getAutomationAnalyticsData } from '../logic/analytics.logic';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { cn } from '@/lib';
import { AUTOMATION_ACTIONS } from '../constants/actions';
import { AutomationActionsMenu } from './AutomationActionsMenu';
import type { AutomationRowProps } from '../types/automation.types';

export const AutomationRow = ({
    automation,
    isLoading = false,
    onClick,
    className,
    isDeleting = false,
    onDelete,
    onPublish,
    onUnpublish,
}: AutomationRowProps) => {
    const isMobile = useIsMobile();

    // Get analytics data
    const analyticsData = useMemo(() => {
        return getAutomationAnalyticsData(automation);
    }, [automation]);

    // Build columns for StanDataRow
    const columns = useMemo(
        () => buildAutomationColumns(automation, analyticsData, isMobile),
        [automation, analyticsData, isMobile]
    );

    // Grid template - single column on mobile, multi-column on desktop
    const gridTemplate = isMobile ? '1fr' : getGridTemplate();

    const handleDropdownAction = (action: string) => {
        const automationId = automation.automation_id;

        switch (action) {
            case AUTOMATION_ACTIONS.PUBLISH:
                onPublish?.(automationId);
                break;
            case AUTOMATION_ACTIONS.UNPUBLISH:
                onUnpublish?.(automationId);
                break;
            case AUTOMATION_ACTIONS.DELETE:
                onDelete?.(automationId);
                break;
            default:
                console.warn('Unknown action:', action);
        }
    };

    // Action buttons for dropdown menu - only show for saved automations
    const isUnsaved = automation.automation_id === undefined;
    const actionButtons: DataRowActionButton[] = isUnsaved
        ? []
        : [
              {
                  id: 'automation-actions',
                  type: 'dropdown',
                  label: 'Automation Actions',
                  menu: <AutomationActionsMenu automation={automation} onAction={handleDropdownAction} />,
              },
          ];

    if (isLoading) {
        return (
            <div className='animate-pulse'>
                <div className='bg-neutral-secondary h-20 rounded-lg' />
            </div>
        );
    }

    return (
        <StanDataRow
            columns={columns}
            gridTemplate={gridTemplate}
            variant='simple'
            actionButtons={actionButtons}
            onClick={onClick}
            className={cn(
                'cursor-pointer transition-all duration-300 ease-out',
                isMobile ? 'automation-row-mobile min-h-32' : 'h-20',
                isDeleting && '-translate-y-5 scale-95 opacity-0',
                className
            )}
        />
    );
};
